import { useState, useEffect, type ChangeEvent } from "react";
import { useAppDispatch } from "../../store/hooks";
import { styled } from "@mui/material/styles";
import axios from "../../plugins/axios";
import MyCKEditor from "../../ckeditor/MyCKEditor";
import { setTemplate } from "../../store/slices/configSlice";
import InsertSection from "./InsertSection";
import InsertPlaceholder from "./InsertPlaceholder";

import { Box, Button as MuiIconButton, TextField } from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { grey } from "@mui/material/colors";

const IconButton = styled(MuiIconButton)`
  padding: 2px;
  margin-top: -2px;
  margin-left: 2px;
  min-width: 0;
  color: ${grey[700]};
`;

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

// configSlice's Template interface has an index sig, so the legacy `html`,
// `templateId`, `name` accesses stay loosely typed via Record.
type EditableTemplate = Record<string, any>;

interface TemplateEditorProps {
    template: EditableTemplate;
    onSave: () => void;
}

const TemplateEditor = ({ template, onSave }: TemplateEditorProps) => {
    const dispatch = useAppDispatch();

    const [templateInfo, setTemplateInfo] = useState<EditableTemplate>(template);
    const [saving, setSaving] = useState(false);
    const [editor, setEditor] = useState<any>();

    const styles = { targetDiv: { height: "calc(100vh - 240px)" } };

    useEffect(() => {
        const html = templateInfo.html ?? "";
        if (editor) editor.setData(html);
    }, [editor, templateInfo.html]);

    useEffect(() => {
        const getTemplateInfo = async () => {
            const response = await axios.get(`${apiUrl}/gettemplate/${template.templateId}`);
            setTemplateInfo({ ...templateInfo, html: response.data.html });
        };

        if (!templateInfo.templateId) {
            setTemplateInfo({ name: "" });
        } else if (!templateInfo.html) {
            getTemplateInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setTemplateName = (e: ChangeEvent<HTMLInputElement>) =>
        setTemplateInfo({ ...templateInfo, name: e.target.value });

    const save = async () => {
        const html = editor.getData();

        if (html === templateInfo.html && templateInfo.name === template.name) {
            onSave();
            return;
        }

        const saveData = { ...templateInfo, html };
        setSaving(true);
        const response = await axios.post(`${apiUrl}/savetemplate`, saveData);
        dispatch(setTemplate(response.data));
        setSaving(false);
        onSave();
    };

    return (
        <Box display="flex" flexDirection="column" style={styles.targetDiv}>
            <Box display="flex" alignItems="center" gap="5px">
                <TextField
                    variant="outlined"
                    size="small"
                    label="Template Name"
                    inputProps={{ maxLength: 255 }}
                    value={templateInfo.name}
                    onChange={setTemplateName}
                    style={{ width: "300px" }}
                />
                {editor && (
                    <IconButton disabled={saving} disableRipple={false} size="large">
                        <SaveIcon onClick={save} />
                    </IconButton>
                )}
            </Box>
            <Box display="flex" gap="5px">
                <InsertSection editor={editor} />
                <InsertPlaceholder editor={editor} />
            </Box>
            <MyCKEditor type="template" setEditor={setEditor} editor={editor} onChange={() => {}} />
        </Box>
    );
};

export default TemplateEditor;
